import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, ArrowUpRight, CheckCircle2, MinusCircle, ShieldX } from "lucide-react";
import { api } from "@/lib/api";
import { Panel } from "@/components/common/Panel";
import { AnimatedNumber } from "@/components/common/AnimatedNumber";
import { staggerContainer, staggerItem } from "@/lib/motion";

const tone = (v) =>
    v === null ? { text: "text-slate-500", bar: "rgba(148,163,184,0.3)", Icon: MinusCircle }
        : v >= 75 ? { text: "text-emerald-400", bar: "var(--safe)", Icon: CheckCircle2 }
            : v >= 45 ? { text: "text-amber-400", bar: "var(--warning)", Icon: AlertTriangle }
                : { text: "text-rose-400", bar: "var(--neon-red)", Icon: ShieldX };

export default function CleanCore() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/posture/clean-core");
                setData(data);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const composite = data?.composite ?? null;
    const ct = tone(composite);
    const dims = data?.dimensions || [];
    const R = 62;
    const C = Math.PI * R; // half-circle arc length

    return (
        <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
        >
            <motion.div variants={staggerItem} className="lg:col-span-1">
                <Panel eyebrow="Composite" title="Clean Core Readiness" testId="clean-core-composite">
                    <div className="relative h-48 flex items-end justify-center">
                        <svg viewBox="0 0 160 90" className="w-full h-full">
                            <path d="M 18 82 A 62 62 0 0 1 142 82" fill="none" stroke="rgba(148,163,184,0.12)" strokeWidth="9" strokeLinecap="round" />
                            <motion.path
                                d="M 18 82 A 62 62 0 0 1 142 82"
                                fill="none" stroke={ct.bar} strokeWidth="9" strokeLinecap="round"
                                strokeDasharray={C}
                                initial={{ strokeDashoffset: C }}
                                animate={{ strokeDashoffset: C - (C * (composite ?? 0)) / 100 }}
                                transition={{ duration: 1.3, ease: [0.22, 1, 0.36, 1] }}
                                style={{ filter: `drop-shadow(0 0 8px ${ct.bar})` }}
                            />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-end pb-2">
                            <div className={`font-display text-5xl ${ct.text}`}>
                                {composite === null ? "—" : <AnimatedNumber value={Math.round(composite)} />}
                            </div>
                            <div className="font-mono text-[10px] text-slate-500 uppercase tracking-wider mt-1">
                                Clean Core / 100
                            </div>
                        </div>
                    </div>
                    <p className="text-xs text-slate-400 mt-3 text-center leading-relaxed">
                        {loading
                            ? "Reading your landscape…"
                            : composite === null
                                ? "Nothing measurable yet — upload SAP artifacts and engineer a role to start scoring."
                                : `Mean of the ${data.measured_dimensions} of ${data.total_dimensions} dimensions that currently have data. Unmeasured dimensions are never guessed.`}
                    </p>
                </Panel>
            </motion.div>

            <motion.div variants={staggerItem} className="lg:col-span-2">
                <Panel eyebrow="Dimensions" title="Where the score comes from" testId="clean-core-dimensions">
                    <ul className="space-y-2.5">
                        {dims.map((d, i) => {
                            const t = tone(d.score);
                            return (
                                <motion.li
                                    key={d.key}
                                    data-testid={`clean-core-dim-${d.key}`}
                                    initial={{ opacity: 0, x: -14 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.12 + i * 0.07, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                    className="panel px-4 py-3 group hover:border-white/15 transition-colors"
                                >
                                    <div className="flex items-center justify-between mb-2 gap-3">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <t.Icon size={14} className={`${t.text} shrink-0`} />
                                            <span className="text-sm text-slate-200 truncate">{d.label}</span>
                                            {!d.measured && (
                                                <span className="font-mono text-[9px] uppercase tracking-wider text-slate-400 border border-white/10 px-1.5 py-0.5 rounded-sm shrink-0">
                                                    not measured
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <div className={`font-display text-lg ${t.text}`}>
                                                {d.score === null ? "—" : Math.round(d.score)}
                                            </div>
                                            <button
                                                data-testid={`clean-core-drill-${d.key}`}
                                                onClick={() => nav(d.drill_to)}
                                                title="Inspect the evidence"
                                                className="text-slate-600 hover:text-cyan-300 transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                <ArrowUpRight size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="h-1 bg-white/8 rounded-full overflow-hidden">
                                        <motion.div
                                            className="h-full rounded-full"
                                            style={{ background: t.bar }}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${d.score ?? 0}%` }}
                                            transition={{ delay: 0.25 + i * 0.07, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                                        />
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-2">{d.basis}</p>
                                </motion.li>
                            );
                        })}
                        {!loading && dims.length === 0 && (
                            <li className="py-8 text-center text-sm text-slate-500" data-testid="clean-core-empty">
                                No dimensions available.
                            </li>
                        )}
                    </ul>
                </Panel>
            </motion.div>
        </motion.div>
    );
}
